<template>
  <v-menu 
    v-bind="attrs.componentAttrs" 
    @update:model-value="onMenuUpdate"
    :offset="prevId ? 8 : 0"
  >
    <template #activator="{ props: menuProps }">
      <!-- We use a bare v-tooltip here because AppTooltip has trouble when combined with another activator -->
      <v-tooltip location="top" transition="fade-transition">
        <template #activator="{ props: tooltipProps }">
          <app-button
            class="add-item-button"
            :class="{ 'only-icon': item }"
            type="icon"
            v-bind="{ ...menuProps, ...tooltipProps }"
            :data-locator="attrs.dataLocator"
          >
            <div class="icon-wrapper" :class="{ 'icon-survol': prevId }">
              <app-material-icon 
                :name="iconName"
                color="grey-600" 
              />
            </div>
            <template v-if="!item">
              <span class="text-grey-800">{{ t('addChecklistItemButtonText') }}</span>
              <div class="icon-wrapper">
                <app-material-icon 
                  name="keyboard_arrow_down" 
                  color="grey-600"
                />
              </div>
            </template>
          </app-button>
        </template>
        <span>{{ t(tooltip) }}</span>
      </v-tooltip>
    </template>
    <v-list data-locator="checklist-add-element-menu">
      <v-list-item
        link
        data-locator="add-task-menu-item"
        :disabled="shouldTaskAdditionBeDisabled"
        @click="handleClickAddItem($event, shouldTaskAdditionBeDisabled, createTask)"
      >
        <v-list-item-title>
          <div class="d-flex align-center">
            <app-material-icon
              name="check_box"
              class="mr-2"            
              color="grey-600"
            />
            <span>{{ t(addTaskTranslationKey) }}</span>
          </div>
        </v-list-item-title>
      </v-list-item>
      <!-- We can't set list item disabled cause the tooltip won't work -->
      <v-list-item
        link
        data-locator="add-section-menu-item"
        :class="{ 'text-greyDark cursor-default': shouldSectionAdditionBeDisabled }"
        @click="handleClickAddItem($event, shouldSectionAdditionBeDisabled, createSection)"
      >
        <AppTooltip v-if="shouldSectionAdditionBeDisabled">
          <template #activator="{ props }">
            <v-list-item-title v-bind="props">
              <app-material-icon
                name="calendar_view_day"
                class="mr-2"
                color="grey-600"
              />
              <span>{{ t(addSectionTranslationKey) }}</span>
            </v-list-item-title>
          </template>
          {{ t('addSectionDisableTooltip') }}
        </AppTooltip>
        <v-list-item-title v-else>
          <app-material-icon
            name="calendar_view_day"
            class="mr-2"
            color="grey-600"
          />
          <span>{{ t(addSectionTranslationKey) }}</span>
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { getLastChild } from '@/common/utils/sections.js'
import { toast } from '@/common/utils/toast'
import { useTranslation } from '@/common/utils/translation'
import { useDataLocator } from '@/common/utils/useDataLocator'
import { CHECKLIST_OPTIONS } from '@/project/checklist/enums'
import { CREATE_TASK, CREATE_SECTION, EXPAND_SECTION } from '@/store/modules/checklist/action_types'
import { add } from 'lodash-es'

export default {
  name: 'ChecklistTableAddButton',
  inheritAttrs: false,
  emits: ['menu-open', 'menu-close'],
  components: {
    AppTooltip,
  },
  props: {
    iconName: {
      type: String,
      default: 'add_2',
    },
    item: {
      type: Object,
      required: false,
    },
    mnemo: {
      type: String,
      required: true,
    },
    prevId: {
      type: [String, Number],
      required: false,
      default: null,
    },
    prevType: {
      type: String,
      required: false,
      default: null,
    },
    prevDepth: {
      type: Number,
      required: false,
      default: null,
    },
    tooltip: {
      type: String,
      default: 'addChecklistItemButtonTooltip',
    },
  },
  setup () {
    const {t} = useTranslation({ keyPrefix: 'project.checklist.ChecklistTableAddButton' })
    const { attrs } = useDataLocator()
    return { attrs, t }
  },
  computed: {
    ...mapGetters('checklist', ['flatChecklist']),
    addSectionTranslationKey () {
      if(!this.item) return 'addSection'

      return this.prevId !== null && this.prevType !== null
        ? 'addSectionAfterItem'
        : 'addSectionIntoSection'
    },
    addTaskTranslationKey () {
      if(!this.item) return 'addTask'
      
      return this.prevId !== null && this.prevType !== null
        ? 'addTaskAfterItem'
        : 'addTaskIntoSection'
    },
    shouldSectionAdditionBeDisabled () {
      // section depth is 0-indexed, hence the increment
      return this.item && this.item.depth >= CHECKLIST_OPTIONS.SECTION_MAX_DEPTH
    },
    shouldTaskAdditionBeDisabled () {
      return this.prevType === 'title' && this.prevDepth === 0
    },
  },
  methods: {
    ...mapActions('checklist', [CREATE_TASK, CREATE_SECTION, EXPAND_SECTION]),

    onMenuUpdate (isOpen) {
      this.$emit(isOpen ? 'menu-open' : 'menu-close')
    },

    /**
     * Base logic for creating a task or section.
     * @param {string} type - The type of checklist item to create ('task' or 'title').
     * @param {Object} defaultData - The default data structure for the checklist item.
     * @param {string} successMessage - The success message to display on creation.
     * @param {string} errorMessage - The error message to display on failure.
     */
    async createChecklistItem (type, defaultData, successMessage, errorMessage) {
      try {
        let data = defaultData

        if (this.item) {
          data = this.prepareAddChecklistItemButtonAction(type)
          data.title = ''
        } else if (this.flatChecklist.length > 0) {
          const lastElement = this.flatChecklist[this.flatChecklist.length - 1]
          data.prevId = lastElement.id
          data.prevType = lastElement.type
        }

        const action = type === 'task' ? this.CREATE_TASK : this.CREATE_SECTION
        await action({ mnemo: this.mnemo, data })
        toast.success(this.t(successMessage))
      } catch (e) {
        console.error(e)
        toast.error(this.t(errorMessage))
      }
    },

    createTask () {
      this.createChecklistItem(
        'task',
        { prevId: null, prevType: null },
        'createTaskSuccess',
        'createTaskError',
      )
    },

    createSection () {
      this.createChecklistItem(
        'title',
        { title: '', depth: 0, prevId: null, prevType: null },
        'createSectionSuccess',
        'createSectionError',
      )
    },

    /**
     * Get the base payload for adding a checklist item.
     * @param {object} [cachedLastChild] - The last child of the item if already computed.
     * @param {string} addItemType - ('title' or 'task').
     * @returns {Object} The payload for the add checklist item button action.
     */
    getAddChecklistItemBasePayload (cachedLastChild, addItemType ) {
      const payload = {
        prevId: this.item.id,
        prevType: this.item.type,
        depth: this.item.depth,
      }

      if (!cachedLastChild) {
        cachedLastChild = getLastChild(this.item).item
      }

      if(this.prevDepth !== null && this.prevType !== null) {
        if(addItemType === 'title' && this.prevType === 'title') {
          payload.depth = this.prevDepth
        } else if (addItemType === 'title' && this.prevType === 'task') {
          payload.depth = this.prevDepth + 1
        } else if (addItemType === 'task' && this.prevType === 'task') {
          payload.depth = this.prevDepth
        } else if (addItemType === 'task' && this.prevType === 'title') {
          payload.depth = this.prevDepth - 1
        }
      }

      payload.prevId = cachedLastChild.id
      payload.prevType = cachedLastChild.type

      return payload
    },

    /**
     * Prepare and retrieve the base payload for any add checklist item action.
     * @param {string} addItemType - The item type to being prepared ('title' or 'task').
     * @returns {Object} The base payload for the action.
     */
    prepareAddChecklistItemButtonAction (addItemType) {
      let lastChild
      if (this.item.type === 'title') {
        const lastSectionChild = getLastChild(this.item, (lastSectionChildResult) => {
          if (addItemType === 'title' && lastSectionChildResult.parent.type === 'task') {
            return
          }
          this.EXPAND_SECTION(lastSectionChildResult.parent.id)
        })
        lastChild = lastSectionChild?.item ?? this.item
      } else {
        lastChild = this.item
      }
      return this.getAddChecklistItemBasePayload(lastChild, addItemType)
    },
    handleClickAddItem (event, isDisabled, callback) {
      if (isDisabled) {
        event.stopPropagation()
      } else {
        callback()
      }
    },
  },
}
</script>
<style scoped lang="scss">
.v-list-item-title {
  display: flex;
  align-items: center;
}

.add-item-button.app-button.app-button--icon {
  min-width: 28px;
  min-height: 28px;
  height: 28px;

  &:not(.only-icon) {
    padding-inline: 12px;
  }

  &.only-icon:not(:has(.icon-survol)):hover {
    background-color: rgb(var(--v-theme-grey-100));
  }

  .icon-wrapper {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.icon-survol:hover {
      background-color: rgba(var(--v-theme-grey-darken-4), 0.05);
      border-radius: 4px;
    }
  }
  
  :deep(span) {
    column-gap: 4px;
  }
}
</style>
